/**
 * Provenienza dei dati mostrati dal grafico (SPEC-016 blocco 5): per ogni
 * fonte il numero di punti, l'intervallo coperto e la tabella di origine, più
 * l'avviso esplicito sulle sedute mancanti. Nessun dato senza provenienza.
 */

import { Alert, Button } from '@/ui';
import { formatDate, formatDateTime, formatNumber } from '@/lib/format';
import type { CoverageEntry, SeriesGap } from './api';

const TABLE_LABELS: Record<CoverageEntry['table'], string> = {
  price_history: 'EOD giornaliero',
  watchlist_price_snapshots: 'Job orario intraday',
};

type Props = {
  coverage: CoverageEntry[] | null;
  gaps: SeriesGap[];
  sourceColors: Record<string, string>;
  onBackfill: () => void;
  onExport: () => void;
  exporting: boolean;
};

export function DataProvenance({ coverage, gaps, sourceColors, onBackfill, onExport, exporting }: Props) {
  const missingSessions = gaps.reduce((total, gap) => total + gap.sessions, 0);

  return (
    <div className="provenance">
      <div className="provenance__row">
        <span className="provenance__title">Provenienza dei dati</span>
        <div className="provenance__actions">
          <Button variant="primary" onClick={onBackfill}>
            Scarica dati aggiuntivi
          </Button>
          <Button variant="secondary" onClick={onExport} loading={exporting}>
            Esporta CSV
          </Button>
        </div>
      </div>

      {coverage === null && (
        <p className="provenance__empty">Copertura non disponibile in questo momento.</p>
      )}
      {coverage !== null && coverage.length === 0 && (
        <p className="provenance__empty">
          Nessun dato di prezzo salvato per questo titolo: lo storico si popola con «Scarica dati
          aggiuntivi» o al prossimo giro del job prezzi.
        </p>
      )}

      {coverage !== null && coverage.length > 0 && (
        <ul className="provenance__list">
          {coverage.map((entry) => (
            <li key={`${entry.table}:${entry.source}`} className="provenance__item">
              <span
                className="provenance__swatch"
                style={{ backgroundColor: sourceColors[entry.source] ?? 'var(--color-text-subtle)' }}
                aria-hidden="true"
              />
              <strong>{entry.source}</strong>
              <span>{TABLE_LABELS[entry.table]}</span>
              <span className="numeric">{formatNumber(entry.points)} punti</span>
              <span className="numeric">
                {entry.table === 'price_history'
                  ? `${formatDate(entry.from)} → ${formatDate(entry.to)}`
                  : `${formatDateTime(entry.from)} → ${formatDateTime(entry.to)}`}
              </span>
              <code className="provenance__table">{entry.table}</code>
            </li>
          ))}
        </ul>
      )}

      {missingSessions > 0 && (
        <Alert variant="warning" title={`${missingSessions === 1 ? 'Una seduta mancante' : `${missingSessions} sedute mancanti`} nell'orizzonte mostrato`}>
          {gaps.slice(0, 3).map((gap) => (
            <span key={gap.from} className="provenance__gap numeric">
              {gap.from === gap.to ? formatDate(gap.from) : `${formatDate(gap.from)} → ${formatDate(gap.to)}`}
            </span>
          ))}
          {gaps.length > 3 && <span> e altri {gaps.length - 3} intervalli.</span>}{' '}
          I tratteggi sul grafico sono interpolazioni solo visive, mai salvate: «Scarica dati
          aggiuntivi» può colmare le sedute davvero quotate.
        </Alert>
      )}
    </div>
  );
}
